import type { DateTime } from "luxon";
import { adjustNightShiftDate } from "./dateUtils";
import type { ShiftTimeOptions } from "./shiftTimeOptions";
import { getShiftIndexByShiftTimes } from "./utilFunctions";

const MINUTES_PER_HOUR = 60;

/** One concrete full-shift option on a staffing day. */
export type ShiftOptionWindow = {
	start: DateTime;
	end: DateTime;
	/** Exact length (e.g. 7.5). Group with `groupShiftOptionWindowsByLength`, which buckets by whole hours. */
	lengthHours: number;
};

export type ShiftOptionWindowGroup = {
	/** Whole hours, floored — the "8H" label the time picker shows beside the row. */
	lengthBucketHours: number;
	/** Ordered by start time. */
	windows: ShiftOptionWindow[];
};

export type BuildShiftOptionWindowsArgs = {
	fullShiftTimes: ShiftTimeOptions["fullShiftTimes"];
	/** The staffing day the options belong to (resolve a wall-clock time with `getShiftDisplayDate` first). */
	staffingDate: DateTime;
	/** Department `dayStart`. */
	dayStart: string;
	/** Scheduled grace (`scheduled_shift_delta_minutes`) or punch grace (`shift_delta_minutes`) for actuals. */
	graceMinutes: number;
};

/**
 * A configured float hour (7.5 = 07:30) as a DateTime on the staffing day. Hours before the effective
 * day start roll to the neighbouring calendar day, so a night option's early-morning end lands on the
 * right date.
 */
export const floatHoursToStaffingDateTime = (hours: number, staffingDate: DateTime, dayStart: string, graceMinutes: number): DateTime => {
	const wholeHours = Math.trunc(hours);
	const minutes = Math.round((hours - wholeHours) * MINUTES_PER_HOUR);
	const time = staffingDate.startOf("day").set({ hour: wholeHours, minute: minutes });
	return adjustNightShiftDate(time, staffingDate, dayStart, graceMinutes);
};

/**
 * The department's full-shift options (from `resolveShiftTimeOptions`) as concrete windows on one
 * staffing day, in the configured order. An end at or before its start crosses midnight, so it moves
 * to the next day — which also makes an equal start/end a 24 h option, as the time picker always had it.
 */
export const buildShiftOptionWindows = ({ fullShiftTimes, staffingDate, dayStart, graceMinutes }: BuildShiftOptionWindowsArgs): ShiftOptionWindow[] =>
	fullShiftTimes.map(([startHours, endHours]) => {
		const start = floatHoursToStaffingDateTime(startHours, staffingDate, dayStart, graceMinutes);
		let end = floatHoursToStaffingDateTime(endHours, staffingDate, dayStart, graceMinutes);
		if (end <= start) {
			end = end.plus({ day: 1 });
		}
		return { start, end, lengthHours: end.diff(start, "hours").as("hours") };
	});

/** Windows bucketed by whole-hour length, shortest bucket first, each bucket ordered by start time. */
export const groupShiftOptionWindowsByLength = (windows: readonly ShiftOptionWindow[]): ShiftOptionWindowGroup[] => {
	const byBucket = new Map<number, ShiftOptionWindow[]>();
	for (const window of windows) {
		const bucket = Math.floor(window.lengthHours);
		const group = byBucket.get(bucket);
		if (group) group.push(window);
		else byBucket.set(bucket, [window]);
	}
	return [...byBucket.entries()]
		.sort(([bucketA], [bucketB]) => bucketA - bucketB)
		.map(([lengthBucketHours, group]) => ({
			lengthBucketHours,
			windows: [...group].sort((a, b) => a.start.valueOf() - b.start.valueOf()),
		}));
};

/** The legacy day (0) / night (1) grid slot a window lands in; the grid still stores shifts by slot. */
export const getShiftIndexForWindow = (window: Pick<ShiftOptionWindow, "start" | "end">): number =>
	getShiftIndexByShiftTimes({ startTime: window.start, endTime: window.end });
